package ru.villawonka.app.platform.telegram.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * This object contains data that is transferred to the Mini App when it is opened.
 * It is empty if the Mini App was launched from a keyboard button or from inline mode.
 * @property queryId A unique identifier for the Mini App session,
 * required for sending messages via the answerWebAppQuery method.
 * @property user An object containing data about the current user.
 * @property receiver An object containing data about the chat
 * partner of the current user in the chat where
 * the bot was launched via the attachment menu.
 * Returned only for private chats and
 * only for Mini Apps launched via the attachment menu.
 * @property chat An object containing data about the chat where
 * the bot was launched via the attachment menu.
 * Returned for supergroups, channels and group chats –
 * only for Mini Apps launched via the attachment menu.
 * @property chatType Type of the chat from which the Mini App was opened.
 * Can be either “sender” for a private chat with the user opening the link,
 * “private”, “group”, “supergroup”, or “channel”.
 * Returned only for Mini Apps launched from direct links.
 * @property chatInstance Global identifier, uniquely corresponding to the chat
 * from which the Mini App was opened. Returned only for
 * Mini Apps launched from a direct link.
 * @property startParam The value of the start_param parameter will
 * also be passed in the GET-parameter tgWebAppStartParam,
 * so the Mini App can load the correct interface right away.
 * @property canSendAfter Time in seconds, after which a message
 * can be sent via the answerWebAppQuery method.
 * @property authDate Unix time when the form was opened.
 * @property hash A hash of all passed parameters, which the bot server
 * can use to check their validity.
 * @property signature A signature of all passed parameters (except hash),
 * which the third party can use to check their validity.
 */
@Serializable
data class WebAppInitData(
    @SerialName("query_id")
    val queryId: String? = null,
    @SerialName("user")
    val user: WebAppUser? = null,
    @SerialName("receiver")
    val receiver: WebAppUser? = null,
    @SerialName("chat")
    val chat: WebAppChat? = null,
    @SerialName("chat_type")
    val chatType: String? = null,
    @SerialName("chat_instance")
    val chatInstance: String? = null,
    @SerialName("start_param")
    val startParam: String? = null,
    @SerialName("can_send_after")
    val canSendAfter: Int? = null,
    @SerialName("auth_date")
    val authDate: Int,
    @SerialName("hash")
    val hash: String,
    @SerialName("signature")
    val signature: String? = null
)